// /agreements — Agreement List per spec §5 + frontend-design §3
// AgreementListFilters / AgreementListTable.
//
// Filters / sort / cursor live in the URL. Cursor-reset rule (F-004) is
// enforced via useUrlState.setFilterOrSort (which strips `cursor`
// before pushing). Pagination's "Show more" advances the cursor only.

import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { agreementsApi, companiesApi } from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { usePrincipal } from '@/auth/PrincipalContext';
import {
  canCreateAgreement,
  isMember,
} from '@/auth/permissions';
import { useUrlState } from '@/utils/useUrlState';
import {
  EmptyState,
  ErrorBanner,
  Pagination,
  Skeleton,
  StatusBadge,
} from '@/components/primitives';
import { ApiError } from '@/types/errors';
import {
  formatCalendarDate,
  formatCurrencyString,
  formatType,
} from '@/utils/format';
import type {
  AgreementSort,
  AgreementStatus,
  AgreementType,
} from '@/types/domain';

const STATUS_OPTIONS: AgreementStatus[] = [
  'draft',
  'active',
  'completed',
  'archived',
];
const TYPE_OPTIONS: AgreementType[] = ['t_and_m', 'fixed_price'];
const SORT_OPTIONS: { value: AgreementSort; label: string }[] = [
  { value: 'status_asc_then_start_date_desc', label: 'Status, then start date' },
  { value: 'start_date_desc', label: 'Start date (newest)' },
  { value: 'start_date_asc', label: 'Start date (oldest)' },
  { value: 'project_name_asc', label: 'Project name (A→Z)' },
  { value: 'project_name_desc', label: 'Project name (Z→A)' },
];

export function AgreementListPage() {
  const principal = usePrincipal();
  const navigate = useNavigate();
  const url = useUrlState();
  const h1Ref = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => h1Ref.current?.focus(), []);

  const statusFilter = url.getAll('status') as AgreementStatus[];
  const typeFilter = url.getAll('type') as AgreementType[];
  const clientId = url.get('client_id');
  const sort = (url.get('sort') as AgreementSort | null) ?? null;
  const cursor = url.get('cursor');

  const effectiveStatus =
    statusFilter.length > 0
      ? statusFilter
      : (['draft', 'active'] as AgreementStatus[]);
  const effectiveType = typeFilter.length > 0 ? typeFilter : undefined;

  const queryArgs = useMemo(
    () => ({
      status: effectiveStatus,
      type: effectiveType,
      client_id: clientId ?? undefined,
      sort: sort ?? undefined,
      cursor: cursor ?? undefined,
    }),
    [clientId, cursor, effectiveStatus, effectiveType, sort],
  );

  const list = useQuery({
    queryKey: qk.agreementsList(queryArgs),
    queryFn: () => agreementsApi.list(queryArgs),
  });

  // Companies for the Client picker — cached aggressively (reference data).
  const companies = useQuery({
    queryKey: qk.companies({ all: true }),
    queryFn: () => companiesApi.list({ limit: 100 }),
    staleTime: 5 * 60_000,
  });

  const create = canCreateAgreement(principal);
  const filtersApplied =
    statusFilter.length > 0 || typeFilter.length > 0 || !!clientId;

  return (
    <div>
      <header className="page-header">
        <h1 ref={h1Ref} tabIndex={-1}>
          Agreements
        </h1>
        <div className="page-actions">
          {create.rendering === 'visible' ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/agreements/new')}
            >
              Create agreement
            </button>
          ) : null}
        </div>
      </header>

      <div className="filter-bar" role="region" aria-label="Filters">
        <fieldset className="filter-group">
          <legend>Status</legend>
          {STATUS_OPTIONS.map((s) => (
            <label key={s} style={{ marginRight: 8, fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={effectiveStatus.includes(s)}
                onChange={() => {
                  const next = new Set(effectiveStatus);
                  if (next.has(s)) next.delete(s);
                  else next.add(s);
                  const arr = Array.from(next);
                  url.setFilterOrSort({ status: arr.length ? arr : null });
                }}
              />{' '}
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </label>
          ))}
        </fieldset>

        <div className="filter-group">
          <label htmlFor="filter-client">Client</label>
          <select
            id="filter-client"
            value={clientId ?? ''}
            disabled={companies.isPending || companies.isError}
            onChange={(e) => {
              url.setFilterOrSort({
                client_id: e.target.value ? e.target.value : null,
              });
            }}
          >
            <option value="">All clients</option>
            {companies.data?.data.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {companies.isError ? (
            <span className="muted">
              Couldn't load clients.{' '}
              <button
                type="button"
                className="btn-link"
                onClick={() => void companies.refetch()}
              >
                Retry
              </button>
            </span>
          ) : null}
        </div>

        <fieldset className="filter-group">
          <legend>Type</legend>
          {TYPE_OPTIONS.map((t) => (
            <label key={t} style={{ marginRight: 8, fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={typeFilter.includes(t)}
                onChange={() => {
                  const next = new Set(typeFilter);
                  if (next.has(t)) next.delete(t);
                  else next.add(t);
                  const arr = Array.from(next);
                  url.setFilterOrSort({ type: arr.length ? arr : null });
                }}
              />{' '}
              {formatType(t)}
            </label>
          ))}
        </fieldset>

        <div className="filter-group">
          <label htmlFor="filter-sort">Sort</label>
          <select
            id="filter-sort"
            value={sort ?? 'status_asc_then_start_date_desc'}
            onChange={(e) => {
              const v = e.target.value as AgreementSort;
              url.setFilterOrSort({
                sort: v === 'status_asc_then_start_date_desc' ? null : v,
              });
            }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {filtersApplied ? (
          <div className="filter-group" style={{ alignSelf: 'end' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => url.clearAll()}
            >
              Clear filters
            </button>
          </div>
        ) : null}
      </div>

      {list.isPending ? (
        <Skeleton rows={5} height="32px" />
      ) : list.isError ? (
        <ListErrorBanner
          error={list.error}
          onRetry={() => void list.refetch()}
          onResetFilters={() => url.clearAll()}
          onResetCursor={() => url.setCursor(null)}
        />
      ) : (list.data?.data.length ?? 0) === 0 ? (
        filtersApplied ? (
          <EmptyState
            message="No agreements match these filters."
            action={
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => url.clearAll()}
              >
                Clear filters
              </button>
            }
          />
        ) : isMember(principal) ? (
          <EmptyState message="You are not assigned to any agreements yet. Contact your administrator if you expected to see one here." />
        ) : (
          <EmptyState
            message="No agreements yet."
            action={
              create.rendering === 'visible' ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate('/agreements/new')}
                >
                  Create agreement
                </button>
              ) : undefined
            }
          />
        )
      ) : (
        <>
          <span aria-live="polite" className="visually-hidden">
            Showing {list.data!.data.length} agreements.
          </span>
          <table className="data-table">
            <caption className="visually-hidden">Agreement list</caption>
            <thead>
              <tr>
                <th scope="col">Project</th>
                <th scope="col">Client</th>
                <th scope="col">Type</th>
                <th scope="col">Status</th>
                <th scope="col">Start date</th>
                <th scope="col">End date</th>
                <th scope="col">Total / Estimated</th>
              </tr>
            </thead>
            <tbody>
              {list.data!.data.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => navigate(`/agreements/${a.id}`)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/agreements/${a.id}`);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <Link to={`/agreements/${a.id}`}>
                      {a.project_name}
                      {a.amendment > 0 ? (
                        <span className="muted"> (Amendment {a.amendment})</span>
                      ) : null}
                    </Link>
                  </td>
                  <td>
                    <ClientName clientId={a.client_id} />
                  </td>
                  <td>{formatType(a.type)}</td>
                  <td>
                    <StatusBadge status={a.status} />
                  </td>
                  <td>{formatCalendarDate(a.start_date)}</td>
                  <td>{formatCalendarDate(a.end_date)}</td>
                  <td>
                    {a.type === 'fixed_price'
                      ? formatCurrencyString(a.total_value)
                      : a.estimated_value
                        ? `${formatCurrencyString(a.estimated_value)} (est.)`
                        : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            nextCursor={list.data!.next_cursor ?? null}
            loading={list.isFetching}
            onNext={() => {
              if (list.data?.next_cursor) url.setCursor(list.data.next_cursor);
            }}
          />
        </>
      )}
    </div>
  );
}

function ClientName({ clientId }: { clientId: string }) {
  const companies = useQuery({
    queryKey: qk.companies({ all: true }),
    queryFn: () => companiesApi.list({ limit: 100 }),
    staleTime: 5 * 60_000,
  });
  const name = companies.data?.data.find((c) => c.id === clientId)?.name;
  return <>{name ?? <span className="muted">Loading…</span>}</>;
}

function ListErrorBanner({
  error,
  onRetry,
  onResetFilters,
  onResetCursor,
}: {
  error: unknown;
  onRetry: () => void;
  onResetFilters: () => void;
  onResetCursor: () => void;
}) {
  if (error instanceof ApiError) {
    if (error.code === 'invalid_cursor') {
      return (
        <ErrorBanner
          message="This page address is no longer valid."
          requestId={error.requestId}
          onRetry={onResetCursor}
          retryLabel="View first page"
        />
      );
    }
    if (error.code === 'invalid_query') {
      return (
        <ErrorBanner
          message="The page address is invalid."
          requestId={error.requestId}
          onRetry={onResetFilters}
          retryLabel="Reset filters"
        />
      );
    }
    if (error.code === 'forbidden') {
      return (
        <ErrorBanner message="You don't have permission to view agreements." />
      );
    }
  }
  return (
    <ErrorBanner
      message="We couldn't load agreements."
      requestId={error instanceof ApiError ? error.requestId : undefined}
      onRetry={onRetry}
    />
  );
}
